import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../contexts/AuthContext';
import axios from '../utils/axios';

// Rol Dialog Bileşeni
const RoleDialog = ({ open, onClose, role, permissions, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        description: role.description || '',
        permissions: role.permissions.map(p => p.id)
      });
    } else {
      setFormData({
        name: '',
        description: '',
        permissions: []
      });
    }
  }, [role]);

  const handleSubmit = async () => {
    try {
      if (!formData.name) {
        setError('Rol adı gerekli');
        return;
      }

      if (role) {
        // Güncelleme
        await onSave({
          id: role.id,
          ...formData
        });
      } else {
        // Yeni rol oluşturma
        await onSave(formData);
      }

      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Bir hata oluştu');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {role ? 'Rolü Düzenle' : 'Yeni Rol Oluştur'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          fullWidth
          label="Rol Adı"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          margin="normal"
          required
        />
        <TextField
          fullWidth
          label="Açıklama"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          margin="normal"
          multiline
          rows={2}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Yetkiler</InputLabel>
          <Select
            multiple
            value={formData.permissions}
            onChange={(e) => setFormData({ ...formData, permissions: e.target.value })}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const permission = permissions.find(p => p.id === value);
                  return (
                    <Chip
                      key={value}
                      label={permission?.name}
                      size="small"
                    />
                  );
                })}
              </Box>
            )}
          >
            {permissions.map((permission) => (
              <MenuItem key={permission.id} value={permission.id}>
                {permission.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>İptal</Button>
        <Button onClick={handleSubmit} variant="contained">
          {role ? 'Güncelle' : 'Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Ana Sayfa Bileşeni
const RolesPage = () => {
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Rolleri getir
  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users/roles');
      console.log('Backend\'den gelen rol verileri:', response.data);
      if (response.data) {
        // Her rolün permissions özelliğinin var olduğundan emin olalım
        const rolesWithPermissions = response.data.map(role => ({
          ...role,
          permissions: role.permissions || []
        }));
        console.log('İşlenmiş rol verileri:', rolesWithPermissions);
        setRoles(rolesWithPermissions);
      } else {
        setRoles([]);
      }
    } catch (err) {
      console.error('Roller yüklenirken hata:', err);
      setError('Roller yüklenirken bir hata oluştu');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  // Yetkileri getir
  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/users/permissions');
      if (response.data) {
        setPermissions(response.data);
      } else {
        setPermissions([]);
      }
    } catch (err) {
      console.error('Yetkiler yüklenirken hata:', err);
      setError('Yetkiler yüklenirken bir hata oluştu');
      setPermissions([]);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  // Rol kaydetme/güncelleme
  const handleSaveRole = async (roleData) => {
    try {
      if (roleData.id) {
        // Güncelleme
        await axios.put(`/users/roles/${roleData.id}`, roleData);
      } else {
        // Yeni rol oluşturma
        await axios.post('/users/roles', roleData);
      }
      fetchRoles();
    } catch (err) {
      throw err;
    }
  };

  // Rol silme
  const handleDeleteRole = async () => {
    try {
      await axios.delete(`/users/roles/${roleToDelete.id}`);
      fetchRoles();
      setDeleteDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Rol silinirken bir hata oluştu');
    }
  };

  if (!hasPermission('USER_MANAGEMENT')) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Bu sayfaya erişim yetkiniz yok.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Rol Yönetimi
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedRole(null);
            setDialogOpen(true);
          }}
        >
          Yeni Rol
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Rol Adı</TableCell>
              <TableCell>Açıklama</TableCell>
              <TableCell>Yetkiler</TableCell>
              <TableCell>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell>{role.name}</TableCell>
                <TableCell>{role.description || '-'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(role.permissions || []).map((permission) => (
                      <Chip
                        key={permission.id}
                        label={permission.name}
                        size="small"
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title="Düzenle">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedRole(role);
                        setDialogOpen(true);
                      }}
                      disabled={role.id === 1} // Admin rolü düzenlenemez
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setRoleToDelete(role);
                        setDeleteDialogOpen(true);
                      }}
                      disabled={role.id === 1} // Admin rolü silinemez
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Rol Oluşturma/Düzenleme Dialog'u */}
      <RoleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        role={selectedRole}
        permissions={permissions}
        onSave={handleSaveRole}
      />

      {/* Rol Silme Dialog'u */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Rolü Sil</DialogTitle>
        <DialogContent>
          <Typography>
            "{roleToDelete?.name}" rolünü silmek istediğinizden emin misiniz?
            Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            İptal
          </Button>
          <Button
            onClick={handleDeleteRole}
            color="error"
            variant="contained"
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RolesPage; 